import React from 'react';
import { useNavigate } from 'react-router-dom';
import { bookmarksApi } from '../../api/bookmarks';
import { chatApi } from '../../api/chat';
import { sessionsApi } from '../../api/sessions';
import { plainSearchSnippet } from '../../utils/plainSearchSnippet';
import { writerFacingRoomName } from '../../utils/roomChat';
import {
  bookmarkRowMessage,
  directMessageTarget,
  rememberRoomMessage,
  roomMessagePath,
  sessionIdFromMessageDetails,
} from '../../utils/searchMessageTarget';
import './Search.css';

const TYPE_CONFIG = {
  message: { icon: '💬', label: 'Message', color: '#3B82F6' },
  summary: { icon: '📄', label: 'Summary', color: '#10B981' },
  bookmark: { icon: '📌', label: 'Bookmark', color: '#F59E0B' }
};

function SearchResult({ result, type, storyId }) {
  const navigate = useNavigate();
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.message;

  const truncateContent = (content, maxLength = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSimilarity = (similarity) => {
    if (similarity === undefined || similarity === null) return 0;
    return Math.round(similarity * 100);
  };

  const openMessage = async (sessionId, messageId) => {
    if (!sessionId || !messageId) return;
    let session = null;
    try {
      const loaded = await sessionsApi.get(sessionId);
      session = loaded?.data?.id ? loaded.data : loaded;
    } catch {
      session = null;
    }
    const storyForRoom = session?.story_id || storyId || null;
    rememberRoomMessage({
      roomKey: session?.room_key,
      storyId: storyForRoom,
      sessionId,
    });
    const path = roomMessagePath({
      roomKey: session?.room_key,
      storyId: storyForRoom,
      projectId: session?.project_id,
      sessionId,
      messageId,
    });
    if (path) navigate(path);
  };

  const handleClick = async () => {
    const direct = directMessageTarget(result, type);
    if (direct?.sessionId && direct?.messageId) {
      await openMessage(direct.sessionId, direct.messageId);
      return;
    }
    if (type !== 'bookmark' || result?.metadata?.is_synthesis || !storyId) return;
    try {
      const rows = await bookmarksApi.list(storyId);
      const match = bookmarkRowMessage(rows, result.id);
      if (!match?.messageId) return;
      let sessionId = match.sessionId;
      if (!sessionId) {
        const details = await chatApi.getMessageDetails(match.messageId);
        sessionId = sessionIdFromMessageDetails(details);
      }
      await openMessage(sessionId, match.messageId);
    } catch {
      /* stay on the results */
    }
  };

  const similarityPercentage = formatSimilarity(result.similarity);
  const sessionLabel = writerFacingRoomName(
    result.metadata?.session_name,
    result.metadata?.room_key,
    result.metadata?.session_type,
  );

  return (
    <div className="search-result" onClick={handleClick}>
      <div className="search-result__header">
        <div className="search-result__type" style={{ backgroundColor: config.color }}>
          <span className="search-result__type-icon">{config.icon}</span>
          <span className="search-result__type-label">{config.label}</span>
        </div>
        <div className="search-result__similarity">
          <div className="search-result__similarity-bar">
            <div
              className="search-result__similarity-fill"
              style={{ width: `${similarityPercentage}%`, backgroundColor: config.color }}
            />
          </div>
          <span className="search-result__similarity-text">{similarityPercentage}%</span>
        </div>
      </div>

      <div className="search-result__content">
        <p className="search-result__text">{truncateContent(plainSearchSnippet(result.content))}</p>
      </div>

      <div className="search-result__metadata">
        {sessionLabel && (
          <span className="search-result__meta-item">
            <span className="search-result__meta-label">Session:</span>
            <span className="search-result__meta-value">{sessionLabel}</span>
          </span>
        )}
        {result.metadata?.created_at && (
          <span className="search-result__meta-item">
            <span className="search-result__meta-label">Date:</span>
            <span className="search-result__meta-value">{formatDate(result.metadata.created_at)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

export default SearchResult;
